import { useCallback, useState } from 'react';

import { fetchSongHistory } from '../api/mobileApi';
import { useAppSelector } from '../app/hooks';
import { selectAppConfig } from '../features/appconfig/store/selectors';
import { selectBearerToken } from '../features/bearertoken/store/selectors';
import { selectCurrentRadioSongHistoryConfig } from '../features/currentradio/store/selectors';
import { SongHistoryEntry, SongHistoryRequestPayload } from '../models';

interface UseFetchSongHistoryReturn {
  songs: SongHistoryEntry[];
  isLoading: boolean;
  error: string | null;
  isNotAvailable: boolean;
  fetchHistory: () => Promise<void>;
}

export const useFetchSongHistory = (
  date?: Date | null,
  time?: Date | null,
  searchTerm?: string,
): UseFetchSongHistoryReturn => {
  const [songs, setSongs] = useState<SongHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNotAvailable, setIsNotAvailable] = useState(false);

  const songHistoryConfig = useAppSelector(selectCurrentRadioSongHistoryConfig);
  const bearerToken = useAppSelector(selectBearerToken);
  const appConfig = useAppSelector(selectAppConfig);

  const fetchHistory = useCallback(async () => {
    // check if song history is configured for the current radio
    if (!songHistoryConfig || !songHistoryConfig.active || !songHistoryConfig.apiUrl) {
      setIsNotAvailable(true);
      setError(null);
      setSongs([]);
      return;
    }

    setIsNotAvailable(false);

    setIsLoading(true);
    setError(null);

    try {
      const payload: SongHistoryRequestPayload = {
        apiUrl: songHistoryConfig.apiUrl,
      };

      if (songHistoryConfig.searchApiUrl) {
        payload.searchApiUrl = songHistoryConfig.searchApiUrl;
      }

      // merge date and time if provided and allowed
      if ((date || time) && songHistoryConfig.dateOptionAvailable) {
        const mergedDate = date ? new Date(date) : new Date();
        if (time) {
          mergedDate.setHours(time.getHours(), time.getMinutes(), time.getSeconds());
        } else if (date) {
          const now = new Date();
          mergedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
        }
        payload.date = mergedDate.toISOString();
      }

      // add search term if provided and allowed
      if (searchTerm && songHistoryConfig.searchOptionAvailable) {
        payload.search = searchTerm;
      }

      const response = await fetchSongHistory(
        payload,
        bearerToken,
        appConfig.idapplications,
      );
      setSongs(response ? [...response] : []);
    } catch (err) {
      setError('failed to fetch song history');
      setSongs([]);
    } finally {
      setIsLoading(false);
    }
  }, [songHistoryConfig, date, time, searchTerm, bearerToken, appConfig.idapplications]);

  return {
    songs,
    isLoading,
    error,
    isNotAvailable,
    fetchHistory,
  };
};
